import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { BookOpen, ChevronDown, ChevronUp, X, MapPin, GraduationCap, BarChart3, Gamepad2, Tag, Flag } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { API } from '../App';

const STRAND_COLORS = {
  Listening: '#4C6EF5',
  Speaking: '#E03131',
  Reading: '#2F9E44',
  Writing: '#F59F00',
};

const CEFR_COLORS = {
  'pre-A1': '#2F9E44',
  'A1': '#4C6EF5',
  'A1-A2': '#F59F00',
  'A2': '#E03131',
};

function OntarioExpectation({ exp }) {
  const [expanded, setExpanded] = useState(false);
  const color = STRAND_COLORS[exp.strand] || '#4C6EF5';

  return (
    <div
      data-testid={`ontario-exp-${exp.code}`}
      style={{
        background: 'var(--surface)',
        border: '2px solid var(--border)',
        borderLeft: `4px solid ${color}`,
        borderRadius: '0.75rem',
        padding: '0.875rem 1rem',
        cursor: 'pointer',
        transition: 'all 0.2s',
      }}
      onClick={() => setExpanded(!expanded)}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1, minWidth: 0 }}>
          <span style={{
            background: color,
            color: 'white',
            padding: '0.15rem 0.5rem',
            borderRadius: '0.375rem',
            fontFamily: 'Fredoka, sans-serif',
            fontWeight: 600,
            fontSize: '0.85rem',
            flexShrink: 0,
          }}>
            {exp.code}
          </span>
          <span style={{
            fontFamily: 'Fredoka, sans-serif',
            fontWeight: 600,
            fontSize: '0.9rem',
            color: 'var(--text-primary)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: expanded ? 'normal' : 'nowrap',
          }}>
            {exp.title || exp.code}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
          <span style={{
            background: `${color}15`,
            color: color,
            padding: '0.15rem 0.5rem',
            borderRadius: '1rem',
            fontSize: '0.8rem',
            fontWeight: 600,
          }}>
            {exp.item_count} items
          </span>
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </div>
      {expanded && exp.text && (
        <div style={{
          marginTop: '0.75rem',
          padding: '0.75rem',
          background: 'var(--bg)',
          borderRadius: '0.5rem',
          fontSize: '0.85rem',
          lineHeight: 1.6,
          color: 'var(--text-secondary)',
          fontFamily: 'Nunito, sans-serif',
        }}>
          <div style={{ fontWeight: 700, color: color, fontSize: '0.75rem', textTransform: 'uppercase', marginBottom: '0.35rem', letterSpacing: '0.05em' }}>
            Ontario — Strand {exp.code[0]}: {exp.strand}
          </div>
          {exp.text}
        </div>
      )}
    </div>
  );
}

function StrandBar({ expectations, totalItems, isLessonContext }) {
  const strands = {};
  for (const exp of expectations) {
    const strand = exp.strand || 'Other';
    // In lesson context every expectation credits the full bank (cross-strand
    // lessons), so summing item_count inflates the bar. Weight by the number
    // of expectations per strand instead — that reflects what mix of skills
    // the lesson author chose and the legend numbers stay honest (count of
    // expectations, summing to data.ontario_expectations.length).
    const weight = isLessonContext ? 1 : exp.item_count;
    strands[strand] = (strands[strand] || 0) + weight;
  }
  const strandOrder = ['Listening', 'Speaking', 'Reading', 'Writing'];
  const totalWeight = Object.values(strands).reduce((a, b) => a + b, 0) || totalItems || 1;
  return (
    <div style={{ display: 'flex', borderRadius: '0.5rem', overflow: 'hidden', height: 8, background: 'var(--border)' }}>
      {strandOrder.map(s => {
        const count = strands[s] || 0;
        if (!count) return null;
        const pct = (count / totalWeight) * 100;
        const tooltip = isLessonContext
          ? `${s}: ${count} expectation${count === 1 ? '' : 's'} (${Math.round(pct)}%)`
          : `${s}: ${count} items (${Math.round(pct)}%)`;
        return (
          <div
            key={s}
            title={tooltip}
            style={{ width: `${pct}%`, background: STRAND_COLORS[s], minWidth: count > 0 ? 4 : 0 }}
          />
        );
      })}
    </div>
  );
}


// ----------- 7-new-province section -----------
// Strand color palette — deterministic by strand name so the same strand gets
// a consistent color across renders (mirrors Ontario's by-strand view).
const STRAND_PALETTE = [
  { bg: '#ECFDF5', fg: '#047857', border: '#10B98140' },  // emerald
  { bg: '#EEF2FF', fg: '#3730A3', border: '#6366F140' },  // indigo
  { bg: '#FFFBEB', fg: '#B45309', border: '#F59E0B40' },  // amber
  { bg: '#FEF2F2', fg: '#B91C1C', border: '#EF444440' },  // rose
  { bg: '#F5F3FF', fg: '#6D28D9', border: '#8B5CF640' },  // violet
  { bg: '#ECFEFF', fg: '#0E7490', border: '#06B6D440' },  // cyan
  { bg: '#FFF7ED', fg: '#9A3412', border: '#F9731640' },  // orange
];
function strandPalette(strand) {
  if (!strand) return { bg: '#F8FAFC', fg: '#475569', border: '#94A3B840' };
  let hash = 0;
  for (let i = 0; i < strand.length; i++) hash = (hash * 31 + strand.charCodeAt(i)) | 0;
  return STRAND_PALETTE[Math.abs(hash) % STRAND_PALETTE.length];
}

// i18n keys for the 7 new-province display labels (used by both Curriculum page
// and the Curriculum Map drawer — single source of truth).
const PROVINCE_I18N_KEY = {
  saskatchewan:   'prog.saskatchewanCurriculum',
  manitoba:       'prog.manitobaCurriculum',
  alberta:        'prog.albertaCurriculum',
  newfoundland:   'prog.newfoundlandCurriculum',
  nova_scotia:    'prog.novaScotiaCurriculum',
  new_brunswick:  'prog.newBrunswickCurriculum',
  pei:            'prog.peiCurriculum',
};

function ProvinceMapSection({ pid, section, t }) {
  const [expanded, setExpanded] = useState(false);
  const i18nKey = PROVINCE_I18N_KEY[pid];
  const label = i18nKey ? t(i18nKey) : section.label_en;
  const source = section.source || {};
  const sourceLine = source.publisher
    ? `${source.publisher}${source.year ? ` · ${source.year}` : ''}`
    : null;

  // Group alignments by curriculum strand (the curriculum's own organization).
  // `strand` comes from the per-province alignment_lookup; never derived.
  const byStrand = {};
  for (const a of section.alignments) {
    const s = a.strand || 'Other';
    if (!byStrand[s]) byStrand[s] = [];
    byStrand[s].push(a);
  }
  const groupOrder = Object.keys(byStrand).sort();

  return (
    <div
      data-testid={`province-section-${pid}`}
      style={{
        background: 'var(--surface)',
        border: '1.5px solid var(--border)',
        borderRadius: '0.625rem',
        overflow: 'hidden',
      }}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0.75rem 0.9rem', background: 'transparent', border: 'none', cursor: 'pointer',
          color: 'var(--text-primary)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
          <Flag size={14} color="#4C6EF5" />
          <span style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: 700, fontSize: '0.92rem' }}>
            {label}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
          <span style={{
            background: 'var(--bg)', color: 'var(--text-secondary)',
            padding: '0.15rem 0.5rem', borderRadius: '1rem',
            fontSize: '0.75rem', fontWeight: 600,
          }}>
            {section.alignments.length} {section.alignments.length === 1 ? 'area' : 'areas'} · {section.total_items} items
          </span>
          {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </div>
      </button>
      {expanded && (
        <div style={{ padding: '0 0.9rem 0.9rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
          {sourceLine && (
            <div style={{
              fontStyle: 'italic', fontSize: '0.72rem', color: 'var(--text-muted)',
              fontFamily: 'Nunito, sans-serif',
            }}>
              {sourceLine}
            </div>
          )}
          {groupOrder.map(strand => {
            const rows = byStrand[strand];
            if (!rows || !rows.length) return null;
            const meta = strandPalette(strand);
            return (
              <div key={strand}>
                <div style={{
                  fontSize: '0.7rem', fontWeight: 700, color: meta.fg,
                  letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: '0.3rem',
                  display: 'flex', alignItems: 'center', gap: '0.3rem',
                }}>
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: meta.fg, display: 'inline-block' }} />
                  {strand}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                  {rows.map((a, i) => (
                    <div
                      key={i}
                      data-testid={`province-${pid}-align-${i}`}
                      style={{
                        display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
                        padding: '0.5rem 0.65rem', borderRadius: '0.5rem',
                        background: meta.bg, border: `1px solid ${meta.border}`,
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0, fontFamily: 'Nunito, sans-serif' }}>
                        <div style={{
                          fontSize: '0.8rem', fontWeight: 700,
                          color: 'var(--text-primary)', lineHeight: 1.3,
                        }}>
                          {a.title || a.label}
                        </div>
                        {a.text && (
                          <div style={{
                            marginTop: '0.2rem',
                            fontSize: '0.75rem', fontWeight: 400,
                            color: 'var(--text-secondary)', lineHeight: 1.4,
                          }}>
                            {a.text}
                          </div>
                        )}
                        {a.title && a.label && a.label !== a.title && (
                          <div style={{
                            marginTop: '0.25rem',
                            fontSize: '0.65rem', fontWeight: 500,
                            color: 'var(--text-muted)', fontStyle: 'italic',
                            letterSpacing: '0.02em',
                          }}>
                            {a.label}
                          </div>
                        )}
                      </div>
                      <span style={{
                        background: 'white', color: meta.fg,
                        padding: '0.1rem 0.4rem', borderRadius: '0.35rem',
                        fontSize: '0.7rem', fontWeight: 700, flexShrink: 0,
                      }}>
                        {a.item_count}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function CurriculumMapPanel({ data, loading, onClose, title }) {
  const { lang, t } = useLanguage();
  if (loading) {
    return (
      <div data-testid="curriculum-map-loading" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
        <div style={{ fontSize: '1.1rem', fontFamily: 'Fredoka, sans-serif' }}>Loading curriculum map...</div>
      </div>
    );
  }

  if (!data || !data.total_items) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
        No curriculum data available for this selection.
      </div>
    );
  }

  return (
    <div data-testid="curriculum-map-panel" style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {/* Pool-fallback notice: lesson context with no dedicated bank for
          this game. The game itself falls back to the shared pool at
          play time (UnifiedGamePlay.js try/catch), so the map honestly
          reports the pool — but we flag it so the teacher knows this
          isn't a hand-curated lesson bank. */}
      {data.lesson_pool_fallback && (
        <div data-testid="curriculum-map-fallback-notice" style={{
          background: 'var(--surface-2)',
          border: '1px solid var(--border)',
          borderLeft: '3px solid #d97706',
          borderRadius: '0.5rem',
          padding: '0.65rem 0.85rem',
          fontSize: '0.8rem',
          color: 'var(--text-muted)',
          lineHeight: 1.4,
        }}>
          This lesson doesn't yet have a hand-curated item bank for this game — the game falls back to the matching browse pool, and these numbers describe that pool.
        </div>
      )}

      {/* Header summary */}
      <div style={{
        background: 'linear-gradient(135deg, #4C6EF5 0%, #3B5BDB 100%)',
        borderRadius: '1rem',
        padding: '1.25rem',
        color: 'white',
      }}>
        <div style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: 700, fontSize: '1.1rem', marginBottom: '0.75rem' }}>
          {title || 'Curriculum Alignment'}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '0.75rem' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontFamily: 'Fredoka, sans-serif', fontWeight: 700 }}>{data.total_items}</div>
            <div style={{ fontSize: '0.75rem', opacity: 0.85 }}>Total Items</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontFamily: 'Fredoka, sans-serif', fontWeight: 700 }}>{data.ontario_expectations.length}</div>
            <div style={{ fontSize: '0.75rem', opacity: 0.85 }}>ON Expectations</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontFamily: 'Fredoka, sans-serif', fontWeight: 700 }}>{data.bc_alignments.length}</div>
            <div style={{ fontSize: '0.75rem', opacity: 0.85 }}>BC Standards</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontFamily: 'Fredoka, sans-serif', fontWeight: 700 }}>{data.cefr_distribution.length}</div>
            <div style={{ fontSize: '0.75rem', opacity: 0.85 }}>CEFR Levels</div>
          </div>
        </div>
      </div>

      {/* Strand distribution bar */}
      <div>
        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.35rem', fontWeight: 600 }}>
          STRAND DISTRIBUTION
        </div>
        <StrandBar expectations={data.ontario_expectations} totalItems={data.total_items} isLessonContext={!!data.lesson_context} />
        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.35rem', flexWrap: 'wrap' }}>
          {['Listening', 'Speaking', 'Reading', 'Writing'].map(s => {
            // In lesson context, count EXPECTATIONS per strand (not items) so
            // the legend doesn't multi-count the same bank items across
            // cross-strand expectations. In browse, each item maps to one
            // expectation so summing item_count is the right number.
            const count = data.lesson_context
              ? data.ontario_expectations.filter(e => e.strand === s).length
              : data.ontario_expectations.filter(e => e.strand === s).reduce((a, e) => a + e.item_count, 0);
            if (!count) return null;
            const label = data.lesson_context
              ? `${s}: ${count} ${count === 1 ? 'expectation' : 'expectations'}`
              : `${s}: ${count}`;
            return (
              <span key={s} style={{ fontSize: '0.75rem', color: STRAND_COLORS[s], fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: STRAND_COLORS[s], display: 'inline-block' }} />
                {label}
              </span>
            );
          })}
        </div>
      </div>

      {/* CEFR levels */}
      <div>
        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.5rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
          <GraduationCap size={14} /> CEFR LEVELS
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {data.cefr_distribution.map(c => (
            <div key={c.level} style={{
              background: `${CEFR_COLORS[c.level] || '#868E96'}15`,
              border: `2px solid ${CEFR_COLORS[c.level] || '#868E96'}40`,
              borderRadius: '0.5rem',
              padding: '0.5rem 0.75rem',
              textAlign: 'center',
              minWidth: 80,
            }}>
              <div style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: 700, fontSize: '1rem', color: CEFR_COLORS[c.level] }}>
                {c.level}
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                {c.label}
              </div>
              <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-primary)', marginTop: '0.15rem' }}>
                {c.item_count} items
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Ontario expectations */}
      <div>
        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.5rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
          <MapPin size={14} /> ONTARIO CURRICULUM EXPECTATIONS
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {data.ontario_expectations.map(exp => (
            <OntarioExpectation key={exp.code} exp={exp} />
          ))}
        </div>
      </div>

      {/* BC alignments */}
      <div>
        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.5rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
          <MapPin size={14} /> BC CURRICULUM ALIGNMENT
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
          {data.bc_alignments.map(bc => (
            <span key={bc.code} data-testid={`bc-align-${bc.code}`} style={{
              background: 'var(--surface)',
              border: '2px solid var(--border)',
              borderRadius: '0.5rem',
              padding: '0.35rem 0.65rem',
              fontSize: '0.8rem',
              fontWeight: 500,
              color: 'var(--text-primary)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.35rem',
            }}>
              {bc.grade && <span style={{ background: '#E7F5FF', color: '#1971C2', padding: '0.1rem 0.3rem', borderRadius: '0.25rem', fontSize: '0.7rem', fontWeight: 700 }}>G{bc.grade}</span>}
              {bc.label}
              <span style={{ color: 'var(--text-muted)', fontSize: '0.7rem' }}>({bc.item_count})</span>
            </span>
          ))}
        </div>
      </div>

      {/* Other provinces (SK / MB / AB / NL / NS / NB / PEI) */}
      {data.provinces && Object.keys(data.provinces).length > 0 && (
        <div>
          <div style={{
            fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.5rem',
            fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.35rem',
          }}>
            <MapPin size={14} /> {lang === 'fr' ? 'AUTRES PROVINCES' : 'OTHER PROVINCES'}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {['saskatchewan','manitoba','alberta','newfoundland','nova_scotia','new_brunswick','pei']
              .filter(pid => data.provinces[pid])
              .map(pid => (
                <ProvinceMapSection key={pid} pid={pid} section={data.provinces[pid]} t={t} />
              ))}
          </div>
        </div>
      )}

      {/* Games covered */}
      {data.games_covered && data.games_covered.length > 1 && (
        <div>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.5rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            <Gamepad2 size={14} /> GAMES COVERED
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
            {data.games_covered.map(g => (
              <span key={g.game} style={{
                background: 'var(--surface-el)',
                borderRadius: '0.5rem',
                padding: '0.35rem 0.65rem',
                fontSize: '0.8rem',
                fontWeight: 600,
                color: 'var(--text-primary)',
              }}>
                {g.name_fr} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({g.item_count})</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Topics covered */}
      {data.topics_covered && data.topics_covered.length > 1 && (
        <div>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.5rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            <Tag size={14} /> TOPICS COVERED
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
            {data.topics_covered.map(t => (
              <span key={t.topic} style={{
                background: 'var(--surface-el)',
                borderRadius: '0.5rem',
                padding: '0.35rem 0.65rem',
                fontSize: '0.8rem',
                fontWeight: 500,
                color: 'var(--text-primary)',
              }}>
                {t.label} <span style={{ color: 'var(--text-muted)' }}>({t.item_count})</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


export function CurriculumMapModal({ isOpen, onClose, queryParams, title }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    if (!isOpen || !queryParams) return;
    setLoading(true);
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(queryParams)) {
      if (v !== null && v !== undefined && v !== '') params.set(k, v);
    }
    axios.get(`${API}/curriculum/map?${params.toString()}`)
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [isOpen, JSON.stringify(queryParams)]);

  if (!isOpen) return null;

  return createPortal(
    <div
      data-testid="curriculum-map-modal"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        display: 'flex',
        justifyContent: 'flex-end',
      }}
    >
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' }}
      />
      {/* Drawer */}
      <div style={{
        position: 'relative',
        width: '100%',
        maxWidth: 520,
        background: 'var(--bg)',
        boxShadow: '-8px 0 32px rgba(0,0,0,0.15)',
        display: 'flex',
        flexDirection: 'column',
        animation: 'slide-in-right 0.3s ease',
      }}>
        {/* Header */}
        <div style={{
          padding: '1rem 1.25rem',
          borderBottom: '2px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'var(--surface)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <BookOpen size={20} color="var(--primary)" />
            <span style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: 700, fontSize: '1.1rem', color: 'var(--text-primary)' }}>
              {t('page.curriculumMap')}
            </span>
          </div>
          <button
            data-testid="curriculum-map-close"
            onClick={onClose}
            style={{
              background: 'var(--surface-el)',
              border: 'none',
              borderRadius: '0.5rem',
              width: 36,
              height: 36,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'var(--text-secondary)',
            }}
          >
            <X size={18} />
          </button>
        </div>
        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '1.25rem' }}>
          <CurriculumMapPanel data={data} loading={loading} onClose={onClose} title={title} />
        </div>
      </div>
      <style>{`
        @keyframes slide-in-right {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </div>,
    document.body
  );
}

export function CurriculumMapButton({ queryParams, title, size = 'normal' }) {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useLanguage();

  const isSmall = size === 'small';

  return (
    <>
      <button
        data-testid="curriculum-map-button"
        onClick={() => setIsOpen(true)}
        title="View Curriculum Alignment"
        style={{
          background: 'var(--surface)',
          border: '2px solid var(--primary)',
          borderRadius: isSmall ? '0.5rem' : '0.75rem',
          padding: isSmall ? '0.35rem 0.65rem' : '0.5rem 1rem',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '0.4rem',
          color: 'var(--primary)',
          fontFamily: 'Fredoka, sans-serif',
          fontWeight: 600,
          fontSize: isSmall ? '0.8rem' : '0.9rem',
          transition: 'all 0.2s',
          boxShadow: '0 2px 6px rgba(76,110,245,0.15)',
        }}
      >
        <BookOpen size={isSmall ? 14 : 16} />
        {!isSmall && t('page.curriculumMap')}
      </button>
      <CurriculumMapModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        queryParams={queryParams}
        title={title}
      />
    </>
  );
}
